/**
 * ChatInput — Text input + send button row.
 *
 * Send button disabled and at 0.4 opacity when input is empty.
 * Touch target 44×44 minimum (WCAG AA).
 */

import React, { useState, useCallback } from 'react';
import { View, TextInput, Pressable, StyleSheet, ViewStyle } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { Ionicons } from '@expo/vector-icons';
import type { TFunction } from '@dramadetector/shared';

const SEND_GRADIENT: [string, string] = ['#EC4899', '#A78BFA'];
const GRADIENT_START = { x: 0, y: 0 } as const;
const GRADIENT_END = { x: 1, y: 1 } as const;

interface ChatInputProps {
  onSend: (text: string) => void;
  t: TFunction<'practitioner'>;
}

export const ChatInput: React.FC<ChatInputProps> = ({ onSend, t }) => {
  const [value, setValue] = useState('');

  const handleSend = useCallback(() => {
    const trimmed = value.trim();
    if (!trimmed) return;
    onSend(trimmed);
    setValue('');
  }, [value, onSend]);

  const isEmpty = value.trim().length === 0;

  return (
    <View style={styles.container}>
      <View style={styles.inputWrapper}>
        <TextInput
          style={styles.input}
          value={value}
          onChangeText={setValue}
          placeholder={t('chat.placeholder')}
          placeholderTextColor="#A1A1AA"
          returnKeyType="send"
          onSubmitEditing={handleSend}
          multiline={false}
        />
      </View>

      <Pressable
        style={[styles.sendButton, isEmpty && styles.sendButtonDisabled]}
        onPress={handleSend}
        disabled={isEmpty}
        accessibilityRole="button"
        accessibilityLabel={t('chat.send')}
        accessibilityState={{ disabled: isEmpty }}
      >
        <LinearGradient
          colors={SEND_GRADIENT}
          start={GRADIENT_START}
          end={GRADIENT_END}
          style={styles.sendGradient}
        >
          <Ionicons name="send" size={16} color="#FFFFFF" />
        </LinearGradient>
      </Pressable>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 12,
    paddingBottom: 12,
  } as ViewStyle,
  inputWrapper: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 12,
    minHeight: 44,
    backgroundColor: 'rgba(241, 245, 249, 0.8)',
    borderWidth: 1,
    borderColor: 'rgba(226, 232, 240, 0.8)',
  } as ViewStyle,
  input: {
    flex: 1,
    fontSize: 14,
    color: '#374151',
    padding: 0,
  },
  sendButton: {
    width: 44,
    height: 44,
    borderRadius: 22,
    overflow: 'hidden',
  },
  sendButtonDisabled: {
    opacity: 0.4,
  },
  sendGradient: {
    width: 44,
    height: 44,
    alignItems: 'center',
    justifyContent: 'center',
  },
});
